import { Button, Card, Dropdown, Menu, Row } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { RouteConstants } from 'app/routes';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { ITableSort } from 'types';
import { useGetEmployeeQuery } from '../../../../api';
import { getEmployeeColumns } from '../../helpers';
import { IEmployee, ITransportFilters } from '../../types';
import EmployeeDrawer from '../EmployeeDrawer/EmployeeDrawer';

interface IProps {
  filters: ITransportFilters;
}

const EmployeeCard: FC<IProps> = ({ filters }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const envId = useSelector(selectSelectedEnvId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [sort, setSort] = useState<ITableSort>({});

  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const { data: employeeResposne, isFetching } = useGetEmployeeQuery(
    {
      companyId,
      companyGroupId,
      envId: envId as number,
      pageSize,
      currentPage: currentPage - 1,
      filters,
      sort,
    },
    { skip: !envId },
  );

  useEffect(() => {
    if (
      employeeResposne?.totalPages &&
      currentPage > employeeResposne.totalPages
    ) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeResposne?.totalPages]);

  const [visibleColKeys, setVisibleColKeys] = useState<string[]>([
    'name',
    'birthYear',
    'slimId',
    'company',
  ]);

  const onVisibleColKeyCheck = (clickedColKey: string) => {
    let updatedArr: string[] = [];
    if (visibleColKeys.includes(clickedColKey)) {
      updatedArr = visibleColKeys.filter(colKey => colKey !== clickedColKey);
    } else {
      updatedArr = [...visibleColKeys, clickedColKey];
    }
    setVisibleColKeys(updatedArr);
  };
  const columns = getEmployeeColumns(t);

  const filteredColumns = columns.filter(col =>
    visibleColKeys.includes(col.key as string),
  );

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: SorterResult<IEmployee> | SorterResult<IEmployee>[],
  ) => {
    if ('field' in sorter && sorter.field && sorter.order) {
      const { field, order } = sorter;
      setSort({ field: field as string | string[], order });
    } else {
      setSort({});
    }

    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return (
    <Card
      bordered={false}
      title={t('Employee.Title', 'Employee list')}
      style={{
        maxHeight: 'calc(100vh - 163px)',
        overflow: 'auto',
        marginLeft: '12px',
      }}
      extra={[
        <PermissionsWrapper
          requiredEditPermissions={[PermissionsEnum.garageAdministration]}
          key="add"
        >
          <Button type="primary" onClick={() => setIsDrawerOpen(true)}>
            {t('Transport.Add', 'Add')}
          </Button>
        </PermissionsWrapper>,
      ]}
    >
      <Row justify="end" style={{ marginBottom: 24 }}>
        <Dropdown
          trigger={['click']}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          overlay={
            <Menu>
              {columns.map(column => (
                <Menu.Item key={column.key}>
                  <Checkbox
                    value={column.key}
                    checked={visibleColKeys.includes(column.key as string)}
                    onChange={() => onVisibleColKeyCheck(column.key as string)}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    {column.title}
                  </Checkbox>
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button>{t('Transport.VisibleColumns', 'Visible columns')}</Button>
        </Dropdown>
      </Row>

      <StyledTable
        onRow={employee => ({
          onClick: () =>
            history.push(
              generatePath(RouteConstants.garage.employeeDetails, {
                envId: envId as number,
                employeeId: employee.id,
                companyId: employee.company.id,
              }),
            ),
        })}
        rowKey="id"
        dataSource={employeeResposne?.content}
        columns={filteredColumns}
        loading={isFetching}
        pagination={{
          current: currentPage,
          total: employeeResposne?.totalElements,
          pageSize,
        }}
        onChange={onTableChange}
      />
      <EmployeeDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </Card>
  );
};

export default EmployeeCard;
